import { useRef } from "react";
import { motion, useMotionValue, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Check, Gauge, TrendingUp } from "lucide-react";
import { scrollToId } from "@/lib/scroll";

const EASE = [0.22, 1, 0.36, 1];

const titleLines = [
  <>Arrêtez de payer</>,
  <>des milliers de dollars</>,
  <>
    pour des sites qui{" "}
    <span className="text-emerald-400">ne vendent pas.</span>
  </>,
];

const reassurance = [
  "Hébergement 100% inclus",
  "Optimisé SEO & mobile",
  "Sans casse-tête",
];

export default function Hero() {
  const sectionRef = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useTransform(my, [-0.5, 0.5], [7, -7]);
  const rotateY = useTransform(mx, [-0.5, 0.5], [-9, 9]);

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end start"],
  });
  const glowY = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const cardY = useTransform(scrollYProgress, [0, 1], [0, 80]);

  const onMouseMove = (e) => {
    const rect = sectionRef.current?.getBoundingClientRect();
    if (!rect) return;
    mx.set((e.clientX - rect.left) / rect.width - 0.5);
    my.set((e.clientY - rect.top) / rect.height - 0.5);
  };

  return (
    <section
      id="hero"
      ref={sectionRef}
      onMouseMove={onMouseMove}
      className="grid-bg relative overflow-hidden pb-24 pt-32 lg:pb-32 lg:pt-44"
      data-testid="hero-section"
    >
      <motion.div
        style={{ y: glowY }}
        className="pointer-events-none absolute -top-40 left-1/2 h-[560px] w-[900px] -translate-x-1/2 rounded-full bg-emerald-500/15 blur-[140px]"
      />

      <div className="relative mx-auto grid max-w-7xl items-center gap-16 px-4 sm:px-6 lg:grid-cols-[1.1fr_0.9fr] lg:px-8">
        <div>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: EASE }}
            className="font-mono-label mb-6 flex items-center gap-3"
            data-testid="hero-tagline"
          >
            <span className="h-px w-8 bg-emerald-400/60" />
            Web & Conversion • Modèle sans risque
          </motion.p>

          <h1
            className="text-4xl font-extrabold leading-[1.08] tracking-tight text-white sm:text-5xl lg:text-6xl"
            data-testid="hero-title"
          >
            {titleLines.map((line, i) => (
              <span key={i} className="block overflow-hidden pb-1">
                <motion.span
                  className="block"
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.9, delay: 0.15 + i * 0.13, ease: EASE }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.65, ease: EASE }}
            className="mt-6 max-w-xl text-base leading-relaxed text-gray-400 sm:text-lg"
            data-testid="hero-subtitle"
          >
            Obtenez un site web sur mesure, rapide et conçu pour convertir vos
            visiteurs en clients payants. Zéro gros chèque initial, hébergement
            gratuit et support continu.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8, ease: EASE }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-cta-pricing"
              onClick={() => scrollToId("tarifs")}
              className="group inline-flex items-center gap-2 rounded-full bg-emerald-500 px-7 py-3.5 text-sm font-semibold text-emerald-950 shadow-[0_8px_32px_rgba(16,185,129,0.35)] transition-all duration-300 hover:bg-emerald-400 hover:shadow-[0_8px_44px_rgba(16,185,129,0.5)]"
            >
              Voir les forfaits & Tarifs
              <ArrowDown className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-0.5" />
            </button>
            <button
              data-testid="hero-cta-why"
              onClick={() => scrollToId("comparatif")}
              className="glass inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-sm font-semibold text-gray-200 transition-colors duration-300 hover:border-emerald-400/40 hover:text-white"
            >
              Pourquoi nous choisir ?
            </button>
          </motion.div>

          <motion.ul
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1 }}
            className="mt-10 flex flex-wrap gap-x-6 gap-y-2"
            data-testid="hero-reassurance"
          >
            {reassurance.map((item) => (
              <li key={item} className="flex items-center gap-2 text-sm text-gray-400">
                <Check className="h-4 w-4 text-emerald-400" />
                {item}
              </li>
            ))}
          </motion.ul>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.5, ease: EASE }}
          style={{ y: cardY, perspective: 1200 }}
          className="relative hidden lg:block"
        >
          <motion.div
            style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
            className="glass emerald-glow relative rounded-2xl p-2"
            data-testid="hero-site-mockup"
          >
            <div className="rounded-xl bg-[#0B1119] p-5">
              <div className="mb-4 flex items-center gap-1.5">
                <span className="h-2.5 w-2.5 rounded-full bg-red-400/70" />
                <span className="h-2.5 w-2.5 rounded-full bg-yellow-400/70" />
                <span className="h-2.5 w-2.5 rounded-full bg-emerald-400/70" />
                <span className="ml-3 h-5 flex-1 rounded-md bg-white/5" />
              </div>
              <div className="space-y-3">
                <div className="h-6 w-3/4 rounded-md bg-gradient-to-r from-emerald-500/40 to-emerald-500/10" />
                <div className="h-3 w-full rounded bg-white/10" />
                <div className="h-3 w-5/6 rounded bg-white/10" />
                <div className="mt-4 grid grid-cols-3 gap-3">
                  {[0, 1, 2].map((i) => (
                    <div key={i} className="h-16 rounded-lg border border-white/5 bg-white/[0.03]" />
                  ))}
                </div>
                <div className="mt-2 h-9 w-40 rounded-full bg-emerald-500/80" />
              </div>
            </div>

            <motion.div
              style={{ transform: "translateZ(50px)" }}
              className="glass absolute -right-6 -top-6 flex items-center gap-2 rounded-xl px-4 py-3"
            >
              <TrendingUp className="h-5 w-5 text-emerald-400" />
              <div>
                <p className="text-sm font-bold text-white">+340%</p>
                <p className="text-[10px] uppercase tracking-wider text-gray-400">conversion</p>
              </div>
            </motion.div>
            <motion.div
              style={{ transform: "translateZ(40px)" }}
              className="glass animate-float-slow absolute -bottom-6 -left-6 flex items-center gap-2 rounded-xl px-4 py-3"
            >
              <Gauge className="h-5 w-5 text-emerald-400" />
              <div>
                <p className="text-sm font-bold text-white">0.9s</p>
                <p className="text-[10px] uppercase tracking-wider text-gray-400">chargement</p>
              </div>
            </motion.div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
